import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PopoverPanel, TriggerButton } from './popover';

// ============================================================================
// TimePicker — the form-controls standard for times (form-controls.md
// "Time: B"): one tap opens a grid of every quarter hour, 5 AM – 10 PM,
// scrolled to the current pick; one more tap and you're done. Replaces
// the three hour / minute / AM-PM selects this file used to be, and every
// <input type="time">. Minutes are still locked to :00 :15 :30 :45 —
// that's the granularity dispatch and payroll work in.
//
// Value contract is unchanged: 'HH:MM' 24-hour string ('' when unset);
// onChange(str). A stored time outside the grid's hours still displays
// on the button and can be re-picked into range.
//
// Props: value, onChange, ariaLabel, size ('md' | 'sm'), `compact` (legacy
// alias for size='sm'), disabled, clearable (default false — most time
// fields are required once a date is set; pass true for optional ones),
// from/to (hours, default 5 and 22), placeholder, className (trigger).
// ============================================================================

export function parseTimeForPicker(t) {
    if (!t) return { h12: '', m15: '', ampm: 'AM' };
    const [h, m] = String(t).split(':').map(Number);
    if (Number.isNaN(h)) return { h12: '', m15: '', ampm: 'AM' };
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const m15 = Math.round((m || 0) / 15) * 15 % 60;
    return { h12, m15, ampm };
}

export function composePickerTime({ h12, m15, ampm }) {
    if (h12 === '' || h12 === null || h12 === undefined) return '';
    const h12Num = Number(h12);
    if (Number.isNaN(h12Num)) return '';
    let h24;
    if (ampm === 'PM') h24 = h12Num === 12 ? 12 : h12Num + 12;
    else h24 = h12Num === 12 ? 0 : h12Num;
    const m = Number(m15) || 0;
    return `${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export const formatTime12 = (t) => {
    if (!t) return '';
    const [h, m] = String(t).split(':').map(Number);
    if (Number.isNaN(h)) return '';
    return `${((h + 11) % 12) + 1}:${String(m || 0).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

const pad = (n) => String(n).padStart(2, '0');

export function TimePicker({ value, onChange, ariaLabel, size, compact = false, disabled = false, clearable = false, from = 5, to = 22, placeholder = 'Pick a time…', className = '' }) {
    const sz = size || (compact ? 'sm' : 'md');
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const gridRef = useRef(null);
    const close = useCallback(() => setOpen(false), []);

    // Normalize the current value onto the :15 grid for highlighting.
    const cur = value ? composePickerTime(parseTimeForPicker(value)) : '';

    // Scroll the current pick (or 8 AM when unset) into the middle.
    useEffect(() => {
        if (!open) return;
        const id = requestAnimationFrame(() => {
            const g = gridRef.current;
            if (!g) return;
            const target = g.querySelector('[data-on="1"]') || g.querySelector('[data-t="08:00"]');
            if (target) target.scrollIntoView({ block: 'center' });
        });
        return () => cancelAnimationFrame(id);
    }, [open]);

    const hours = [];
    for (let h = from; h <= to; h++) hours.push(h);

    return (
        <>
            <TriggerButton
                innerRef={anchorRef}
                onClick={() => !disabled && setOpen(o => !o)}
                open={open}
                size={sz}
                disabled={disabled}
                placeholder={placeholder}
                empty={!value}
                ariaLabel={ariaLabel}
                className={className}
                glyph={(
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>
                )}
            >
                {formatTime12(value)}
            </TriggerButton>
            <PopoverPanel open={open} onClose={close} anchorRef={anchorRef} width={272} ariaLabel={ariaLabel ? `${ariaLabel} — choose a time` : 'Choose a time'} className="overflow-hidden">
                <div ref={gridRef} className="grid grid-cols-4 gap-1 max-h-[248px] overflow-auto p-1.5">
                    {hours.map(h => (
                        <React.Fragment key={h}>
                            <div className="col-span-4 font-mono text-[10px] tracking-[0.14em] text-slate-400 px-1 pt-1.5 pb-0.5 uppercase">{((h + 11) % 12) + 1} {h >= 12 ? 'PM' : 'AM'}</div>
                            {[0, 15, 30, 45].map(m => {
                                const t = `${pad(h)}:${pad(m)}`;
                                const on = t === cur;
                                return (
                                    <button
                                        key={t}
                                        type="button"
                                        data-t={t}
                                        data-on={on ? '1' : '0'}
                                        aria-label={formatTime12(t)}
                                        aria-pressed={on}
                                        onClick={() => { onChange?.(t); close(); }}
                                        className={`h-9 rounded-lg border text-[13px] tabular-nums transition-colors ${
                                            on ? 'bg-red-600 border-red-600 text-white font-bold' : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50'
                                        }`}
                                    >
                                        :{pad(m)}
                                    </button>
                                );
                            })}
                        </React.Fragment>
                    ))}
                </div>
                {clearable && value && (
                    <div className="border-t border-slate-100 p-1.5">
                        <button type="button" onClick={() => { onChange?.(''); close(); }} className="w-full h-8 rounded-lg text-[12px] font-semibold text-red-600 hover:bg-red-50">Clear time</button>
                    </div>
                )}
            </PopoverPanel>
        </>
    );
}

export default TimePicker;
